/**
 * Texture Loader for Hybrid Wheel
 * Preloads all 16 PNG layers before game initialization
 */

import * as PIXI from 'pixi.js';
import type { WheelTextures } from './WheelRendererHybrid';

/**
 * Asset paths (relative to runtime-assets base)
 * Cache busting: Add ?v= timestamp to force reload after PNG updates
 */
const CACHE_VERSION = Date.now(); // Will change on each build

const TEXTURE_PATHS = {
  shadow: '/runtime-assets/games/wheel_of_fortune/v1/01_shadow.png',
  rimBase: '/runtime-assets/games/wheel_of_fortune/v1/02_rim_base_gold.png',
  rimHighlight: '/runtime-assets/games/wheel_of_fortune/v1/03_rim_highlight.png',
  bulbsOff: '/runtime-assets/games/wheel_of_fortune/v1/04_bulbs_off.png',
  bulbsOn: '/runtime-assets/games/wheel_of_fortune/v1/05_bulbs_on.png',
  centerBase: '/runtime-assets/games/wheel_of_fortune/v1/06_center_base.png',
  centerGloss: '/runtime-assets/games/wheel_of_fortune/v1/07_center_gloss.png',
  pointer: '/runtime-assets/games/wheel_of_fortune/v1/08b_pointer_gold_base.png',
  segmentsBase: `/runtime-assets/games/wheel_of_fortune/v1/09_segments_base.png?v=${CACHE_VERSION}`, // Cache bust!
  segmentsGloss: '/runtime-assets/games/wheel_of_fortune/v1/10_segments_gloss.png',
  innerShadowRing: '/runtime-assets/games/wheel_of_fortune/v1/11_inner_shadow_ring.png',
  centerShadow: '/runtime-assets/games/wheel_of_fortune/v1/12_center_shadow.png',
  centerInnerHighlight: '/runtime-assets/games/wheel_of_fortune/v1/13_center_inner_highlight.png',
  segmentSeparators: '/runtime-assets/games/wheel_of_fortune/v1/14_segment_separators.png',
  textShadowEmboss: '/runtime-assets/games/wheel_of_fortune/v1/15_text_shadow_emboss.png',
  wheelAmbientShadow: '/runtime-assets/games/wheel_of_fortune/v1/16_wheel_ambient_shadow.png',
  // NEW: Center button detailed layers (17-22)
  centerShadowOuter: '/runtime-assets/games/wheel_of_fortune/v1/17_center_shadow_outer.png',
  centerRingGold: '/runtime-assets/games/wheel_of_fortune/v1/18_center_ring_gold.png',
  centerFaceBase: '/runtime-assets/games/wheel_of_fortune/v1/19_center_face_base.png',
  centerFaceInnerShadow: '/runtime-assets/games/wheel_of_fortune/v1/20_center_face_inner_shadow.png',
  centerGlassHighlight: '/runtime-assets/games/wheel_of_fortune/v1/21_center_glass_highlight.png',
  centerInnerHighlightV2: '/runtime-assets/games/wheel_of_fortune/v1/22_center_inner_highlight.png',
};

/**
 * Preload all wheel textures
 * 
 * @returns Promise<WheelTextures> - Map of all loaded textures
 */
export async function loadWheelTextures(): Promise<WheelTextures> {
  console.log('[TextureLoader] Loading 22 wheel textures...');
  
  try {
    // Create array of all texture paths
    const paths = Object.values(TEXTURE_PATHS);
    
    // Preload all textures in parallel
    const loadedTextures = await PIXI.Assets.load<PIXI.Texture>(paths);
    
    // Map loaded textures to WheelTextures interface
    const textures: WheelTextures = {
      shadow: loadedTextures[TEXTURE_PATHS.shadow],
      rimBase: loadedTextures[TEXTURE_PATHS.rimBase],
      rimHighlight: loadedTextures[TEXTURE_PATHS.rimHighlight],
      bulbsOff: loadedTextures[TEXTURE_PATHS.bulbsOff],
      bulbsOn: loadedTextures[TEXTURE_PATHS.bulbsOn],
      centerBase: loadedTextures[TEXTURE_PATHS.centerBase],
      centerGloss: loadedTextures[TEXTURE_PATHS.centerGloss],
      pointer: loadedTextures[TEXTURE_PATHS.pointer],
      segmentsBase: loadedTextures[TEXTURE_PATHS.segmentsBase],
      segmentsGloss: loadedTextures[TEXTURE_PATHS.segmentsGloss],
      innerShadowRing: loadedTextures[TEXTURE_PATHS.innerShadowRing],
      centerShadow: loadedTextures[TEXTURE_PATHS.centerShadow],
      centerInnerHighlight: loadedTextures[TEXTURE_PATHS.centerInnerHighlight],
      segmentSeparators: loadedTextures[TEXTURE_PATHS.segmentSeparators],
      textShadowEmboss: loadedTextures[TEXTURE_PATHS.textShadowEmboss],
      wheelAmbientShadow: loadedTextures[TEXTURE_PATHS.wheelAmbientShadow],
      // NEW: Center button detailed layers
      centerShadowOuter: loadedTextures[TEXTURE_PATHS.centerShadowOuter],
      centerRingGold: loadedTextures[TEXTURE_PATHS.centerRingGold],
      centerFaceBase: loadedTextures[TEXTURE_PATHS.centerFaceBase],
      centerFaceInnerShadow: loadedTextures[TEXTURE_PATHS.centerFaceInnerShadow],
      centerGlassHighlight: loadedTextures[TEXTURE_PATHS.centerGlassHighlight],
      centerInnerHighlightV2: loadedTextures[TEXTURE_PATHS.centerInnerHighlightV2],
    };
    
    // Validate all textures loaded
    const missingTextures = Object.entries(textures)
      .filter(([_, texture]) => !texture)
      .map(([key]) => key);
    
    if (missingTextures.length > 0) {
      throw new Error(`Failed to load textures: ${missingTextures.join(', ')}`);
    }
    
    console.log('[TextureLoader] ✅ All 16 textures loaded successfully');
    
    return textures;
  } catch (error) {
    console.error('[TextureLoader] ❌ Failed to load textures:', error);
    throw error;
  }
}

/**
 * Get texture load progress (for loading UI)
 */
export function getTextureLoadProgress(): number {
  // This is a simple implementation - PIXI.Assets doesn't expose granular progress
  // You can enhance this with manual tracking if needed
  return 1.0; // For now, return 100% when complete
}
